import { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Chip } from '@mui/material';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import SyncIcon from '@mui/icons-material/Sync';
import ErrorIcon from '@mui/icons-material/Error';

/**
 * AutoSaveIndicator - shows the current auto-save status
 */
const AutoSaveIndicator = ({ 
  isSaving, 
  hasUnsavedChanges, 
  lastSaved, 
  error,
  getLastSavedText,
  variant = 'chip' // 'chip' | 'text' | 'icon'
}) => {
  const [displayText, setDisplayText] = useState('');

  // Update display text periodically
  useEffect(() => {
    const updateText = () => {
      if (error) {
        setDisplayText('Save failed');
      } else if (isSaving) {
        setDisplayText('Saving...');
      } else if (hasUnsavedChanges) {
        setDisplayText('Unsaved changes');
      } else if (getLastSavedText) {
        setDisplayText(getLastSavedText());
      } else {
        setDisplayText('All changes saved');
      }
    };

    updateText();
    const interval = setInterval(updateText, 10000); // Update every 10s

    return () => clearInterval(interval);
  }, [isSaving, hasUnsavedChanges, lastSaved, error, getLastSavedText]);

  const getIcon = () => {
    if (error) return <ErrorIcon sx={{ fontSize: 16 }} />;
    if (isSaving) return <SyncIcon sx={{ fontSize: 16, animation: 'spin 1s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />;
    if (hasUnsavedChanges) return <CloudOffIcon sx={{ fontSize: 16 }} />;
    return <CloudDoneIcon sx={{ fontSize: 16 }} />;
  };

  const getColor = () => {
    if (error) return 'error';
    if (isSaving) return 'info';
    if (hasUnsavedChanges) return 'warning';
    return 'success';
  };

  if (variant === 'icon') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', color: `${getColor()}.main` }}>
        {isSaving ? (
          <CircularProgress size={16} color="inherit" />
        ) : (
          getIcon()
        )}
      </Box>
    );
  }

  if (variant === 'text') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {isSaving ? (
          <CircularProgress size={14} />
        ) : (
          getIcon()
        )}
        <Typography variant="caption" color={`${getColor()}.main`}>
          {displayText}
        </Typography>
      </Box>
    );
  }

  // Default: chip variant
  return (
    <Chip
      size="small"
      icon={isSaving ? <CircularProgress size={14} /> : getIcon()}
      label={displayText}
      color={getColor()}
      variant="outlined"
      sx={{ 
        height: 24,
        '& .MuiChip-label': { px: 1, fontSize: 12 },
        '& .MuiChip-icon': { ml: 0.5 },
      }}
    />
  );
};

export default AutoSaveIndicator;

